// Relation index for simple statement rows: which movements are linked
// (payments ↔ invoices) on the current page. Pure domain — no React — so the
// cross-reference behavior stays unit-testable, like `cliente-tabs.ts`.
import type { EstadoCuentaSimpleMovement } from '@aid/shared';

/** One counterpart movement a row relates to. */
export interface RelationTarget {
  /** Counterpart movement id: `factura:<id>`, `pago:<id>`, `excepcion:<id>`. */
  id: string;
  /** True when the counterpart row is present in the current page. */
  onPage: boolean;
}

/** Relation entry for one movement row. */
export interface MovementRelation {
  /** Deduplicated counterpart targets, sorted by numeric counterpart id. */
  targets: RelationTarget[];
  /** Targets whose counterpart row is on the current page. */
  relatedOnPageIds: string[];
}

/**
 * Build a symmetric, deduplicated relation index for the movements of the
 * current page. A pair is recorded from either direction — PAGO rows expose
 * `aplicadoA` (counterpart `factura:<idFactura>`) and FACTURA rows expose
 * `pagadoPor` (counterpart `pago:<idPago>`) — so both movements get the pair
 * even when one direction is absent. Movements with no relations are not in
 * the map; readers use `map.get(id)` and fall back to empty.
 */
/** Movement id a paying source points at: the payment row that paid the invoice, the
 * exception row that over-reduced it, or the credit lot's own movement. */
function pagoSourceTarget(item: {
  idPago?: number;
  source?: string;
  idReferencia?: number;
  idCredito?: number;
}): string | null {
  if (item.source === 'EXCEPCION') {
    return item.idReferencia != null ? `excepcion:${item.idReferencia}` : null;
  }
  if (item.source === 'CREDITO') {
    return item.idCredito != null ? `credito:${item.idCredito}` : null;
  }
  const id = item.idPago ?? item.idReferencia;
  return id != null ? `pago:${id}` : null;
}

export function buildRelationIndex(
  rows: EstadoCuentaSimpleMovement[],
): Map<string, MovementRelation> {
  const onPageIds = new Set(rows.map((row) => row.id));
  const rawTargets = new Map<string, string[]>();
  for (const row of rows) {
    for (const applied of row.aplicadoA ?? []) {
      addPair(rawTargets, row.id, `factura:${applied.idFactura}`);
    }
    for (const paidBy of row.pagadoPor ?? []) {
      const target = pagoSourceTarget(paidBy);
      if (target) addPair(rawTargets, row.id, target);
    }
  }

  const index = new Map<string, MovementRelation>();
  for (const row of rows) {
    const targetIds = rawTargets.get(row.id);
    if (!targetIds || targetIds.length === 0) continue;
    // Deduplicate and sort by numeric counterpart id ascending. Targets of a
    // given row share one counterpart kind, so the prefix is constant here.
    const targets = [...new Set(targetIds)].sort(
      (a, b) => counterpartNumber(a) - counterpartNumber(b),
    );
    const resolved = targets.map((id) => ({ id, onPage: onPageIds.has(id) }));
    index.set(row.id, {
      targets: resolved,
      relatedOnPageIds: resolved.filter((target) => target.onPage).map((target) => target.id),
    });
  }
  return index;
}

/** Record an undirected pair in both directions, deduplicated per row. */
function addPair(targets: Map<string, string[]>, a: string, b: string): void {
  for (const [row, counterpart] of [
    [a, b],
    [b, a],
  ] as const) {
    const list = targets.get(row) ?? [];
    if (!list.includes(counterpart)) list.push(counterpart);
    targets.set(row, list);
  }
}

/** Numeric id after the `kind:` prefix; 0 for ids without a numeric part. */
function counterpartNumber(id: string): number {
  const n = Number(id.slice(id.indexOf(':') + 1));
  return Number.isFinite(n) ? n : 0;
}
